import React, { useEffect, useRef, useState, useCallback, useImperativeHandle, forwardRef } from 'react';
import { Avatar, CircularProgress } from './ui';
import { Search, Store } from 'lucide-react';
import type { TypeaheadHit } from '../services/algoliaSearch';
import { useSearchStore } from '@/stores/searchStore';
import { resolveImageUrl, PLACEHOLDER_LOGO } from '../utils/image';

interface SearchAutocompleteProps {
  inputValue: string;
  onSelect: (hit: TypeaheadHit) => void;
  onSearchAll: (query: string) => void;
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  activeIndex: number;
  setActiveIndex: (i: number) => void;
}

export interface SearchAutocompleteHandle {
  /** Total selectable items ("Search all" + hits) */
  itemCount: number;
  /** Trigger the action for the currently active index */
  selectActive: () => boolean;
}

const DEBOUNCE_MS = 250;

const SearchAutocomplete = forwardRef<SearchAutocompleteHandle, SearchAutocompleteProps>(function SearchAutocomplete({
  inputValue,
  onSelect,
  onSearchAll,
  anchorEl,
  open,
  onClose,
  activeIndex,
  setActiveIndex,
}, ref) {
  const [hits, setHits] = useState<TypeaheadHit[]>([]);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef(0);
  const userLocation = useSearchStore((s) => s.userLocation);

  // Debounced typeahead search
  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);

    const trimmed = inputValue.trim();
    if (trimmed.length < 2) {
      setHits([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const requestId = ++abortRef.current;

    timerRef.current = setTimeout(async () => {
      try {
        const loc = userLocation;
        const aroundLatLng = loc ? `${loc[0]},${loc[1]}` : undefined;
        const { searchTypeahead } = await import('../services/algoliaSearch');
        const results = await searchTypeahead(trimmed, aroundLatLng);
        if (abortRef.current === requestId) {
          setHits(results);
          setLoading(false);
        }
      } catch {
        if (abortRef.current === requestId) {
          setHits([]);
          setLoading(false);
        }
      }
    }, DEBOUNCE_MS);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [inputValue, userLocation]);

  // Reset active index when hits change
  useEffect(() => {
    setActiveIndex(-1);
  }, [hits, setActiveIndex]);

  // Expose imperative handle for Enter key selection from parent
  useImperativeHandle(ref, () => ({
    itemCount: hits.length + 1,
    selectActive: () => {
      if (activeIndex === 0) {
        onSearchAll(inputValue.trim());
        return true;
      }
      const hitIdx = activeIndex - 1;
      if (hitIdx >= 0 && hitIdx < hits.length) {
        onSelect(hits[hitIdx]);
        return true;
      }
      return false;
    },
  }), [hits, activeIndex, inputValue, onSearchAll, onSelect]);

  if (!open || inputValue.trim().length < 2) return null;

  // Total items: hits + "Search all" option
  const totalItems = hits.length + 1;

  return (
    <div className="absolute top-full left-0 right-0 z-[1300] mt-1 max-h-[400px] overflow-auto rounded-lg bg-card-bg shadow-2xl">
      <div>
        {/* Search all results option */}
        <button
          className={`w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-hover-bg transition-colors ${
            activeIndex === 0 ? 'bg-hover-bg' : ''
          }`}
          onMouseDown={(e) => { e.preventDefault(); onSearchAll(inputValue.trim()); }}
        >
          <Search className="w-5 h-5 text-gray-400 flex-shrink-0" />
          <div className="text-sm text-text-primary">
            Search all for "<strong>{inputValue.trim()}</strong>"
          </div>
        </button>

        {/* Loading indicator */}
        {loading && hits.length === 0 && (
          <div className="flex justify-center py-4">
            <CircularProgress size={20} />
          </div>
        )}

        {/* Typeahead hits */}
        {hits.map((hit, i) => {
          const imgSrc = resolveImageUrl(
            hit.imageUrl || hit.logoUrl || hit.image || '',
            PLACEHOLDER_LOGO,
          );
          return (
            <button
              key={hit.id}
              className={`w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-hover-bg transition-colors ${
                activeIndex === i + 1 ? 'bg-hover-bg' : ''
              }`}
              onMouseDown={(e) => { e.preventDefault(); onSelect(hit); }}
            >
              <Avatar
                src={imgSrc}
                className="w-9 h-9 rounded flex-shrink-0"
              >
                <Store className="w-4 h-4" />
              </Avatar>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-text-primary truncate">{hit.name}</div>
                <div className="text-xs text-text-secondary truncate">
                  {hit.city && hit.state
                    ? `${hit.city}, ${hit.state}`
                    : hit.city || hit.state || ''}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
});

export default SearchAutocomplete;
